import React from "react";
import { connect } from "react-redux";

import { Route, Switch, useRouteMatch } from "react-router-dom";

import PermissionName from "@mapmycustomers/shared/enum/PermissionName";

import MileageTrackingFilePage from "@app/scene/reports/components/MileageTracking/FilePage";
import { reportsExportsAnalytics } from "@app/scene/reports/reportsAnalytics";
import { hasPermission, isMileageTrackingVisible } from "@app/store/iam";
import { RootState } from "@app/store/rootReducer";
import PermissionGetter from "@app/types/PermissionGetter";
import AnalyticsContext from "@app/util/contexts/AnalyticsContext";

import ReportType from "../../enum/ReportType";

import ActivityCard from "./components/Activity";
import DashboardList from "./components/dashboards/DashboardList";
import PinnedDashboard from "./components/dashboards/PinnedDashboard";
import PromotionalCumulDashboard from "./components/dashboards/PromotionalCumulDashboard";
import DealsCard from "./components/Deals";
import ExportCard from "./components/Export";
import MileageTrackingPage from "./components/MileageTracking";
import MileageTrackingUpsell from "./components/MileageTracking/components/MileageTrackingUpsell";
import Overview from "./components/Overview";
import CreateReport from "./components/ReportEdit/CreateReport";
import EditReport from "./components/ReportEdit/EditReport";
import ReportScreen from "./components/ReportScreen";
import WeeklyReportsCard from "./components/WeeklyReports";
import { isStackRankAvailable } from "./store/selectors";

export const LeaderboardCardPage = React.lazy(
  () =>
    import(
      /* webpackPrefetch: true */
      "./components/Leaderboard"
    )
);

type RouteParams = {
  reportType: ReportType;
};

interface Props {
  hasPermission: PermissionGetter;
  isMileageTrackingVisible: boolean;
  isStackRankAvailable: boolean;
}

const ReportsPage: React.FC<Props> = ({
  hasPermission,
  isMileageTrackingVisible,
  isStackRankAvailable,
}) => {
  const { path } = useRouteMatch<RouteParams>();

  return (
    <Switch>
      <Route exact path={`${path}/dashboard/:id?`}>
        <ReportScreen reportType={ReportType.PINNED_DASHBOARD}>
          {() => <PinnedDashboard />}
        </ReportScreen>
      </Route>
      <Route exact path={`${path}/upgrade-your-reports`}>
        <ReportScreen reportType={ReportType.UPGRADE_YOUR_REPORTS}>
          {() => <PromotionalCumulDashboard />}
        </ReportScreen>
      </Route>
      <Route exact path={`${path}/all-dashboards`}>
        <ReportScreen reportType={ReportType.ALL_DASHBOARDS}>
          {() => <DashboardList />}
        </ReportScreen>
      </Route>
      <Route exact path={`${path}/overview`}>
        <ReportScreen reportType={ReportType.OVERVIEW}>
          {(props) => <Overview {...props} />}
        </ReportScreen>
      </Route>
      <Route exact path={`${path}/activity`}>
        <ReportScreen reportType={ReportType.ACTIVITY}>
          {(props) => <ActivityCard {...props} />}
        </ReportScreen>
      </Route>
      <Route exact path={`${path}/deals`}>
        <ReportScreen reportType={ReportType.DEALS}>
          {(props) => <DealsCard {...props} />}
        </ReportScreen>
      </Route>
      <Route exact path={`${path}/mileage-tracking`}>
        <ReportScreen reportType={ReportType.MILEAGE_TRACKING}>
          {(props) =>
            isMileageTrackingVisible ? (
              <MileageTrackingPage {...props} />
            ) : (
              <MileageTrackingUpsell />
            )
          }
        </ReportScreen>
      </Route>
      <Route
        exact
        path={`${path}/mileage-tracking/:tid/files/:fileId`}
        component={MileageTrackingFilePage}
      />
      {hasPermission(PermissionName.EXPORT_READ) && (
        <Route exact path={`${path}/exports`}>
          <ReportScreen reportType={ReportType.EXPORTS}>{() => <ExportCard />}</ReportScreen>
        </Route>
      )}
      {hasPermission(PermissionName.EXPORT_CREATE) && (
        <Route exact path={`${path}/exports/create`}>
          <AnalyticsContext.Provider value={reportsExportsAnalytics}>
            <ReportScreen reportType={ReportType.EXPORT_EDIT}>
              {() => <CreateReport />}
            </ReportScreen>
          </AnalyticsContext.Provider>
        </Route>
      )}
      {hasPermission(PermissionName.EXPORT_UPDATE) && (
        <Route exact path={`${path}/exports/:exportId`}>
          <ReportScreen reportType={ReportType.EXPORT_EDIT}>
            {({ reportId }) => <EditReport reportId={reportId!} />}
          </ReportScreen>
        </Route>
      )}
      <Route exact path={`${path}/weekly-report-card`}>
        <ReportScreen reportType={ReportType.WEEKLY_REPORT_CARD}>
          {() => <WeeklyReportsCard />}
        </ReportScreen>
      </Route>
      {isStackRankAvailable && (
        <Route component={LeaderboardCardPage} exact path={`${path}/stack-rank`} />
      )}

      {/* TODO: add default case? */}
    </Switch>
  );
};

const mapStateToProps = (state: RootState) => ({
  hasPermission: hasPermission(state),
  isMileageTrackingVisible: isMileageTrackingVisible(state),
  isStackRankAvailable: isStackRankAvailable(state),
});

export default connect(mapStateToProps)(ReportsPage);
